// src/components/blog/templates/BlogHomepageSimple.tsx

'use client'

import { useMemo, useState } from 'react'
import type { BlogPost, Topic } from '~/lib/blog/types'
import { formatDate } from '~/lib/utils/date'
import { NewsletterSection } from '~/components/blog/features/NewsletterSection'
import { cn } from '~/lib/utils'
import Link from 'next/link'
import { IconComponent } from '~/components/shared/IconComponent'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { motion, AnimatePresence } from 'motion/react'
import { BlogHero } from '~/components/blog/features/BlogHeroSimple'
import { LatestPostsLayout } from '~/components/blog/layouts/LatestPosts'
import { BlogImage } from '~/components/blog/components/BlogImage'
import { HomeTopicsGrid } from '~/components/blog/features/HomeTopicsGrid'
import { getBlogListingImageUrl } from '~/lib/blog/utils/listingImage'

interface BlogHomeProps {
  featuredPosts: BlogPost[]
  latestPosts: BlogPost[]
  trendingPosts: BlogPost[]
  trendingTopics: Topic[]
  className?: string
}

// Only the sort modes backed by real data on `latestPosts` (plain `BlogPost[]`
// carries a publish date but NO engagement metrics). "Most Popular" / "Trending"
// were removed rather than shipped as no-ops: the only source of those metrics
// (`getTrendingPosts`) currently fabricates them with `Math.random`, so sorting
// by them would present fabricated rankings.
type SortOption = 'newest' | 'oldest'

const sortOptions: Record<SortOption, { label: string; icon: "RiTimeLine" | "RiArrowDownSLine" }> = {
  newest: { label: 'Newest First', icon: 'RiTimeLine' },
  oldest: { label: 'Oldest First', icon: 'RiTimeLine' }
}

export function BlogHome({
  featuredPosts,
  latestPosts,
  trendingPosts,
  trendingTopics,
  className,
}: BlogHomeProps): JSX.Element {
  const [sortBy, setSortBy] = useState<SortOption>('newest')
  const [dropdownOpen, setDropdownOpen] = useState(false)

  // Actually apply the selected sort (the dropdown previously updated the label
  // only, leaving the list untouched). Both modes sort by the real publish date.
  const sortedPosts = useMemo(() => {
    const posts = [...latestPosts]
    posts.sort((a, b) => {
      const delta = new Date(a.datetime).getTime() - new Date(b.datetime).getTime()
      return sortBy === 'newest' ? -delta : delta
    })
    return posts
  }, [latestPosts, sortBy])

  return (
    <div className={cn("min-h-screen bg-gray-50 dark:bg-gray-900", className)}>
      <main className="relative pb-16">
        {/* Featured Section */}
        <section className="relative bg-gray-50 py-8 dark:bg-gray-900">
          {/* Gradient overlay that fades from transparent at top to dark at bottom */}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-gray-800/50 to-gray-900 dark:from-transparent dark:via-gray-800/70 dark:to-gray-900" />
          
          <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-12 max-w-3xl space-y-5">
              <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl dark:text-white">
                AdSights Blog: Creative Analytics & Performance Marketing Insights
              </h1>
              <p className="text-xl leading-relaxed text-gray-700 dark:text-gray-300">
                Expert guides, campaign benchmarks, and creative intelligence research to help you ship smarter, higher-performing marketing.
              </p>
            </div>
            <div className="grid gap-8 lg:grid-cols-2">
              {/* Featured Post */}
              <div className="relative h-full">
                {featuredPosts[0] && (
                  <BlogHero post={featuredPosts[0]} />
                )}
              </div>

              {/* Featured Topics */}
              <div className="h-full">
                <HomeTopicsGrid topics={trendingTopics.slice(0, 3)} />
              </div>
            </div>
          </div>
        </section>

        {/* Content Sections */}
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-12">
            {/* Main Content */}
            <div className="lg:col-span-8">
              {/* Latest Posts */}
              <section>
                <div className="mb-8 flex items-center justify-between">
                  <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-3xl">
                    Latest Articles
                  </h2>
                  <DropdownMenu.Root open={dropdownOpen} onOpenChange={setDropdownOpen}>
                    <DropdownMenu.Trigger asChild>
                      <button className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700">
                        <IconComponent name={sortOptions[sortBy].icon} className="h-4 w-4" />
                        {sortOptions[sortBy].label}
                        <IconComponent 
                          name="RiArrowDownSLine" 
                          className={cn(
                            "h-4 w-4 transition-transform",
                            dropdownOpen && "rotate-180"
                          )}
                        />
                      </button>
                    </DropdownMenu.Trigger>

                    <AnimatePresence>
                      {dropdownOpen && (
                        <DropdownMenu.Portal forceMount>
                          <DropdownMenu.Content asChild>
                            <motion.div
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              transition={{ duration: 0.2 }}
                              className="z-50 mt-1 min-w-[180px] origin-top-right rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800"
                            >
                              {(Object.entries(sortOptions) as [SortOption, typeof sortOptions[SortOption]][]).map(([key, option]) => (
                                <DropdownMenu.Item
                                  key={key}
                                  onSelect={() => setSortBy(key)}
                                  className={cn(
                                    "flex items-center gap-2 px-4 py-2 text-sm text-gray-700 outline-none dark:text-gray-300",
                                    "hover:bg-gray-100 dark:hover:bg-gray-700",
                                    "cursor-pointer",
                                    key === sortBy && "bg-blue-50 text-blue-600 dark:bg-blue-900/50 dark:text-blue-400"
                                  )}
                                >
                                  <IconComponent name={option.icon} className="h-4 w-4" />
                                  {option.label}
                                </DropdownMenu.Item>
                              ))}
                            </motion.div>
                          </DropdownMenu.Content>
                        </DropdownMenu.Portal>
                      )}
                    </AnimatePresence>
                  </DropdownMenu.Root>
                </div>

                <LatestPostsLayout posts={sortedPosts} />
              </section>
            </div>

            {/* Sidebar */}
            <aside className="lg:col-span-4">
              {/* Trending Posts */}
              <section className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white sm:text-xl">
                  Trending Now
                </h3>
                <div className="space-y-8">
                  {trendingPosts.slice(0, 4).map((post) => (
                    <article key={post.id} className="group">
                      <Link
                        href={post.href}
                        className="flex gap-6"
                      >
                        <div className="relative h-24 w-24 flex-none overflow-hidden rounded-lg sm:h-32 sm:w-32">
                          <BlogImage
                            src={getBlogListingImageUrl(post)}
                            alt={post.title}
                            aspectRatio="1/1"
                            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                            sizes="(min-width: 640px) 128px, 96px"
                          />
                        </div>
                        <div className="flex flex-col justify-between py-1">
                          <h4 className="text-sm font-medium text-gray-900 group-hover:text-blue-600 dark:text-white dark:group-hover:text-blue-400 line-clamp-3 sm:text-lg">
                            {post.title}
                          </h4>
                          <div className="flex items-center gap-3">
                            <div className="relative h-6 w-6 overflow-hidden rounded-full ring-2 ring-white/10">
                              <BlogImage
                                src={post.author.imageUrl}
                                alt={post.author.name}
                                aspectRatio="1/1"
                                className="rounded-full"
                                sizes="24px"
                              />
                            </div>
                            <time dateTime={post.datetime} className="text-sm text-gray-500 dark:text-gray-400">
                              {/* UTC-pinned: consistent server/client day, stable fingerprint. */}
                              {formatDate(post.date, 'shortMonthDay')}
                            </time>
                          </div>
                        </div>
                      </Link>
                    </article>
                  ))}
                </div>
              </section>

              {/* Newsletter */}
              <div className="mt-12">
                <NewsletterSection compact />
              </div>
            </aside>
          </div>
        </div>
      </main>
    </div>
  )
} 